import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import Icon from "@msi/cobalt-react/icon";
import "bootstrap/dist/css/bootstrap.min.css";
import JiraIcon from '../../assets/icons/jira-icon.svg';
import ConfluenceIcon from '../../assets/icons/confluence-icon.svg';
import GoogleChatIcon from '../../assets/icons/google-chat-icon.svg';
import { useModalManager } from '../ModalManager/ModalManager';
import { useDarkTheme } from '../../contexts/DarkThemeContext';

const AboutDropDown: React.FC = () => {
  const supportEmail = process.env.REACT_APP_SUPPORT_EMAIL;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const { openModal } = useModalManager();
  const { isDarkTheme } = useDarkTheme();
  const navigate = useNavigate();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const toggleDropdown = () => setIsDropdownOpen((open) => !open);

  const handleWatchVideoGuide = () => {
    setIsDropdownOpen(false);
    openModal('videoTutorial');
  };

  const handleReleaseNotes = () => {
    setIsDropdownOpen(false);
    navigate('/release-notes');
  };

  // Get version directly from package.json
  const currentVersion = process.env.REACT_APP_VERSION || "unknown";

  return (
    <div
      className="dropdown-trigger-wrapper"
      style={{ position: "relative", display: "inline-block" }}
      ref={triggerRef}
    >
      <div
        className="dropdown-trigger msi-common-header-dropdown me-2"
        onClick={toggleDropdown}
        aria-expanded={isDropdownOpen}
        style={{ cursor: "pointer" }}
      >
        <Icon
          name="ic_information"
          size={30}
          className="msi-icon"
        />
      </div>
      {isDropdownOpen && (
        <ul className="dropdown-menu show">
          <li>
            <span className="dropdown-item-text fw-bold" style={{ fontSize: '15px' }}>About API Hub</span>
          </li>
          <li>
            <span className="dropdown-item-text" style={{ fontSize: '15px' }}>Current version: {currentVersion}</span>
          </li>
          <li>
            <button
              type="button"
              className="dropdown-item d-flex align-items-center"
              onClick={handleReleaseNotes}
            >
              <Icon
                name="ic_file_cabinet"
                size={19}
                color={isDarkTheme ? '#e0e0e0' : '#212529'}
                style={{ width: 18, height: 18, marginRight: 8, flexShrink: 0 }}
              />
              Release notes
            </button>
          </li>
          <li><hr className="dropdown-divider" /></li>
          <li>
            <span className="dropdown-item-text fw-bold" style={{ fontSize: '13px' }}>Learning Resources</span>
          </li>
          <li>
            <button
              type="button"
              className="dropdown-item d-flex align-items-center"
              onClick={handleWatchVideoGuide}
            >
              <Icon
                name="ic_attachment_video"
                size={18}
                color={isDarkTheme ? '#e0e0e0' : '#212529'}
                style={{ width: 18, height: 18, marginRight: 8, flexShrink: 0 }}
              />
              Video Guide
            </button>
          </li>
          <li>
            <a
              className="dropdown-item d-flex align-items-center"
              href="https://confluence.mot-solutions.com/display/MSIAPI/MSI+API+Hub+-+User+Guide"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={ConfluenceIcon} alt="Confluence" style={{ width: 18, height: 18, marginRight: 8, flexShrink: 0 }} />
              User Guide
            </a>
          </li>
          <li>
            <span className="dropdown-item-text fw-bold" style={{ fontSize: '13px' }}>Need Assistance or Report Bug?</span>
          </li>
          <li>
            <a
              className="dropdown-item d-flex align-items-center"
              href={`https://mail.google.com/mail/?view=cm&to=${supportEmail}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon
                name="ic_email"
                size={18}
                color={isDarkTheme ? '#e0e0e0' : '#212529'}
                style={{ width: 18, height: 18, marginRight: 8, flexShrink: 0 }}
              />
              {supportEmail}
            </a>
          </li>
          <li>
            <a
              className="dropdown-item d-flex align-items-center"
              href="https://chat.google.com/room/AAQArz4bews?cls=7"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={GoogleChatIcon} alt="Google Chat" style={{ width: 18, height: 18, marginRight: 8, flexShrink: 0 }} />
              API Hub Community Channel
            </a>
          </li>
          <li>
            <a
              className="dropdown-item d-flex align-items-center"
              href="https://jira.mot-solutions.com/secure/CreateIssue.jspa?pid=53202&issuetype=10000"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={JiraIcon} alt="Jira" style={{ width: 18, height: 18, marginRight: 8, flexShrink: 0 }} />
              API Hub Jira Service Desk
            </a>
          </li>
        </ul>
      )}
    </div>
  );
};

export default AboutDropDown;
